/**
 * Marks a record surface as showing a DRAFT — a pending proposal projected onto
 * a record — and carries the small amount of extra information the sections
 * need to render it as one.
 *
 * ## Why a context and not props
 *
 * The projected object already flows down as `object`, so sections render the
 * post-apply state without knowing anything about proposals. Only two extra
 * facts are needed, and only by a couple of leaf renderers deep in the tree:
 * which properties changed (to draw a before/after) and whether the record
 * exists yet (to suppress affordances that would write to it). Threading those
 * through every intermediate section as props would touch a dozen components
 * that have no interest in them; a context lets the two that care opt in.
 *
 * Outside a draft, `useRecordDraft()` returns `null` and every consumer takes
 * its existing path — which is what keeps the live record surface unchanged.
 */

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

import { createContext, type ReactNode, useContext, useMemo } from 'react';

import type { AssociationChange } from '../buildProjectedObject';

export interface RecordDraftValue {
  /**
   * Prior values for the properties the proposal changes, keyed by property
   * name. Presence of a key means "render a before/after for this row";
   * absence means the property is untouched and renders plainly. Empty while
   * creating, because there is no before.
   */
  previousValues: Record<string, unknown>;
  /**
   * What the proposal does to each association target, keyed by
   * `target_entity_id` — the association counterpart of `previousValues`.
   * Presence of a key means "mark this badge"; absence means the association
   * is untouched and renders plainly.
   */
  associationChanges: Record<string, AssociationChange>;
  /**
   * True when the record does not exist on the server yet.
   *
   * This is NOT the same as "has a pending proposal". A pending change on a
   * live record is still a draft, but the record is real: its timeline,
   * associations and files are all valid and should render. Only when this is
   * true must persistence-dependent sections and affordances stay out.
   */
  isUnsaved: boolean;
  /** The proposal being previewed, so the footer can commit or discard it. */
  proposalId: number | undefined;
}

const RecordDraftContext = createContext<RecordDraftValue | null>(null);

export interface RecordDraftProviderProps extends RecordDraftValue {
  /**
   * When false, the context provides `null` (live-record behavior) while
   * keeping the provider mounted, so consumers don't remount as a draft
   * toggles on/off. Defaults to true.
   */
  active?: boolean;
  children: ReactNode;
}

export function RecordDraftProvider({
  previousValues,
  associationChanges,
  isUnsaved,
  proposalId,
  active = true,
  children,
}: RecordDraftProviderProps) {
  const value = useMemo<RecordDraftValue | null>(
    () =>
      active
        ? { previousValues, associationChanges, isUnsaved, proposalId }
        : null,
    [active, previousValues, associationChanges, isUnsaved, proposalId]
  );
  return (
    <RecordDraftContext.Provider value={value}>
      {children}
    </RecordDraftContext.Provider>
  );
}

/**
 * The draft this record surface is showing, or `null` on a live record.
 *
 * Consumers must treat `null` as "behave exactly as before" — this hook is
 * additive, and the live slideout renders with no provider above it.
 */
export function useRecordDraft(): RecordDraftValue | null {
  return useContext(RecordDraftContext);
}
